// WHAT: Card component for displaying and editing MiracleFlow affirmations.
// WHY:  Provides interface for viewing, creating, and editing affirmations with validation and type-specific styling.
// DOMAIN: Productivity → MiracleFlow
// DEPENDENCIES: React, @/components/ui, @tracq/frontend-shared/utils, ../atoms/Icon, ../../types, ../../utils/validators, lucide-react
// RISKS:
//   - WARN: Validation uses validateAffirmation utility; ensure it matches backend validation rules.
//   - NOTE: Type-specific colors are hardcoded; changes require code updates.
//   - WARN: Edit mode state is managed locally; unsaved changes may be lost on mode switch.

import { Edit, Trash2, Save, X, Heart, AlertCircle } from "lucide-react";
import React, { useState } from "react";

import { Card, Button, Input, Badge, Stack, Heading } from "@/components/ui";
import { cn } from "@/shared/utils";

import { type Affirmation, type AffirmationType } from "../../types";
import { validateAffirmation } from "../../utils/validators";

export interface AffirmationCardProperties {
  affirmation?: Affirmation;
  className?: string;
  mode?: "create" | "edit" | "view";
  onCancel?: () => void;
  onDelete?: () => void;
  onEdit?: () => void;
  onSave?: (text: string) => void;
  type: AffirmationType;
}

/**
 * WHAT:
 *   Card component for displaying and editing MiracleFlow affirmations.
 *
 * @returns JSX.Element
 *
 * WHY:
 *   Provides users with interface for viewing, creating, and editing affirmations
 *   with validation and type-specific styling (morning, evening, general).
 *
 * PROPS:
 *   - affirmation?: Affirmation - Optional affirmation object to display/edit
 *   - type: AffirmationType - Type of affirmation (morning, evening, general)
 *   - mode?: 'view' | 'edit' | 'create' - Display mode (default: 'view')
 *   - onSave?: (text: string) => void - Callback when affirmation is saved
 *   - onEdit?: () => void - Callback to enter edit mode
 *   - onDelete?: () => void - Callback to delete affirmation
 *   - onCancel?: () => void - Callback to cancel editing
 *   - className?: string - Optional additional CSS classes
 *
 * BEHAVIOR:
 *   - Displays affirmation text in view mode
 *   - Provides input field for editing/creating in edit/create mode
 *   - Validates affirmation text before saving
 *   - Shows error messages for invalid input
 *   - Applies type-specific gradient colors and labels
 *   - Provides action buttons (edit, delete, save, cancel) based on mode
 *   - Resets edit state on cancel
 *
 * RISKS:
 *   - WARN: Validation uses validateAffirmation utility; ensure it matches backend validation rules.
 *   - NOTE: Type-specific colors are hardcoded; changes require code updates.
 *   - WARN: Edit mode state is managed locally; unsaved changes may be lost on mode switch.
 *   - NOTE: Error state is cleared on cancel; validation errors may be lost.
 *
 * @param root0
 *
 * @param root0.affirmation
 *
 * @param root0.type
 *
 * @param root0.mode
 *
 * @param root0.onSave
 *
 * @param root0.onEdit
 *
 * @param root0.onDelete
 *
 * @param root0.onCancel
 *
 * @param root0.className
 *
 */
export const AffirmationCard: React.FC<AffirmationCardProperties> = ({
  affirmation,
  className,
  mode = "view",
  onCancel,
  onDelete,
  onEdit,
  onSave,
  type,
}) => {
  const [editText, setEditText] = useState(affirmation?.text ?? "");
  const [isValid, setIsValid] = useState(true);
  const [error, setError] = useState<string | undefined>();

  /**
   * WHAT:
   *   Handles saving affirmation with validation.
   *
   * WHY:
   *   Ensures only valid affirmations are saved, preventing invalid data from being persisted.
   *
   * SIDE EFFECTS:
   *   - Validates editText using validateAffirmation utility
   *   - Sets isValid and error states based on validation result
   *   - Calls onSave callback with validated text if valid
   *
   * RISKS:
   *   - WARN: Validation depends on validateAffirmation utility; ensure it's up-to-date.
   *   - NOTE: Error message defaults to 'Invalid affirmation' if validation.error is missing.
   */
  const handleSave = () => {
    const validation = validateAffirmation(editText);
    if (!validation.isValid) {
      setIsValid(false);
      setError(validation.error ?? "Invalid affirmation");
      return;
    }

    setIsValid(true);
    setError(undefined);
    onSave?.(editText);
  };

  /**
   * WHAT:
   *   Handles canceling edit/create mode and resetting state.
   *
   * WHY:
   *   Allows users to discard changes and return to view mode or empty state.
   *
   * SIDE EFFECTS:
   *   - Resets editText to original affirmation text or empty string
   *   - Clears validation and error states
   *   - Calls onCancel callback
   */
  const handleCancel = () => {
    setEditText(affirmation?.text ?? "");
    setIsValid(true);
    setError(undefined);
    onCancel?.();
  };

  /**
   * WHAT:
   *   Returns gradient color classes based on affirmation type.
   *
   * WHY:
   *   Provides visual distinction between different affirmation types.
   *
   * OUTPUT:
   *   - string - Tailwind CSS gradient classes
   *
   * RISKS:
   *   - NOTE: Colors are hardcoded; changes require code updates.
   *
   * @returns Tailwind CSS gradient classes
   *
   */
  const getTypeColor = (): string => {
    switch (type) {
      case "morning": {
        return "from-tracq-deep-blue to-tracq-bright-blue";
      }
      case "evening": {
        return "from-tracq-deep-orange to-tracq-light-orange";
      }
      default: {
        return "from-[#334155] to-[#475569]";
      }
    }
  };

  /**
   * WHAT:
   *   Returns label text based on affirmation type.
   *
   * WHY:
   *   Provides human-readable labels for different affirmation types.
   *
   * OUTPUT:
   *   - string - Type label
   *
   * @returns Type label string
   *
   */
  const getTypeLabel = (): string => {
    switch (type) {
      case "morning": {
        return "Morning";
      }
      case "evening": {
        return "Evening";
      }
      default: {
        return "Unknown";
      }
    }
  };

  return (
    <Card
      className={cn(
        "bg-[#1E293B] border-[#334155] transition-all duration-200 hover:shadow-tracq-card",
        mode === "edit" && "ring-2 ring-tracq-bright-blue/30",
        className,
      )}
      padding="md"
      variant="default"
    >
      <Stack direction="col" gap={3}>
        {/* Header */}
        <Stack align="center" direction="row" justify="between">
          <Stack align="center" direction="row" gap={3}>
            <div
              className={cn(
                "flex items-center justify-center w-10 h-10 rounded-tracq bg-gradient-to-br shadow-tracq-card",
                getTypeColor(),
              )}
            >
              <Heart className="w-5 h-5 text-white" />
            </div>
            <Stack direction="col" gap={1}>
              <Heading className="text-[#F1F5F9] font-semibold" level="h4" size="sm">
                {mode === "create" ? "New Affirmation" : "Affirmation"}
              </Heading>
              <Badge
                className={cn(
                  type === "morning" &&
                    "bg-tracq-bright-blue/20 text-tracq-bright-blue border-tracq-bright-blue/30",
                  type === "evening" &&
                    "bg-tracq-light-orange/20 text-tracq-light-orange border-tracq-light-orange/30",
                )}
                size="sm"
                variant="info"
              >
                {getTypeLabel()}
              </Badge>
            </Stack>
          </Stack>

          {mode === "view" && (
            <Stack direction="row" gap={2}>
              <Button
                className="border-[#334155] text-[#CBD5E1] hover:bg-[#334155] hover:border-tracq-bright-blue/50"
                onClick={onEdit}
                size="sm"
                variant="outline"
              >
                <Edit className="w-4 h-4" />
              </Button>
              <Button
                className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                onClick={onDelete}
                size="sm"
                variant="outline"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </Stack>
          )}
        </Stack>

        {/* Content */}
        {mode === "view" ? (
          <div className="p-4 bg-[#0F172A] rounded-tracq border border-[#334155]">
            <p className="text-[#CBD5E1] leading-relaxed">
              {affirmation?.text ?? "No affirmation text"}
            </p>
          </div>
        ) : (
          <Stack direction="col" gap={3}>
            <Input
              className={cn(
                "bg-[#334155] border-[#475569] text-[#F1F5F9] placeholder:text-[#94A3B8]",
                !isValid && "border-red-500/50",
              )}
              onChange={(e) => {
                setEditText(e.target.value);
              }}
              placeholder="Enter your affirmation..."
              value={editText}
            />

            {error ? (
              <Stack align="center" direction="row" gap={2}>
                <AlertCircle className="w-4 h-4 text-red-500" />
                <p className="text-red-300 text-sm">{error}</p>
              </Stack>
            ) : null}

            <Stack className="justify-end" direction="row" gap={2}>
              <Button
                className="border-[#334155] text-[#CBD5E1] hover:bg-[#334155]"
                onClick={handleCancel}
                size="sm"
                variant="outline"
              >
                <X className="w-4 h-4 mr-2" />
                Cancel
              </Button>
              <Button
                className={cn(
                  "text-white font-semibold",
                  type === "morning"
                    ? "bg-gradient-to-r from-tracq-deep-blue to-tracq-bright-blue hover:from-tracq-deep-blue/90 hover:to-tracq-bright-blue/90"
                    : "bg-gradient-to-r from-tracq-deep-orange to-tracq-light-orange hover:from-tracq-deep-orange/90 hover:to-tracq-light-orange/90",
                )}
                disabled={!editText.trim()}
                onClick={handleSave}
                size="sm"
                variant="primary"
              >
                <Save className="w-4 h-4 mr-2" />
                Save
              </Button>
            </Stack>
          </Stack>
        )}
      </Stack>
    </Card>
  );
};
